import { getVariantPath } from '@uswitch/ev-utils/lib/get-variant-path'
import { useEntities, useEntity, useMeta } from '@uswitch/ev-utils/lib/context/selectors'
import { contentTypeId } from '@uswitch/ev-utils/lib/contentful'
import { Type } from '@uswitch/spark'
import ImageModule from '../image-module'

const stylePath = (field, variant) =>
  getVariantPath({
    atomicType: 'modules',
    componentId: 'subNavigation',
    variantName: variant,
    field,
  })

const SubNavigationCollection = ({ lists = [], image }) => {
  const linkListEntities = useEntities(lists).map(({ links, subtitle }) => ({
    links,
    subtitle,
  }))

  const imageEntity = useEntity(image)

  const imageStyles = {
    position: ['relative', 'absolute', 'absolute'],
    right: '0',
    bottom: '0',
    maxHeight: '100%',
    maxWidth: '100%',
    width: ['100%', 'auto', 'auto'],
    height: 'auto',
    margin: ['auto', '0 0 0 0', '0 0 0 0'],
  }

  return (
    <div sx={{ display: ['block', 'flex', 'flex'] }}>
      <div
        sx={{
          width: image ? ['100%', '70%', '70%'] : '100%',
          display: 'grid',
          gridTemplateColumns: [
            '1fr',
            image ? 'repeat(2,minmax(0, 1fr))' : 'repeat(4,minmax(0, 1fr))',
            image ? 'repeat(2,minmax(0, 1fr))' : 'repeat(4,minmax(0, 1fr))',
          ],
          msGridColumns: ['1fr', '(minmax(0, 1fr))[4]', '(minmax(0, 1fr))[4]'],
          columnGap: '5%',
          flexBasis: '70%',
        }}
      >
        <For each='listEntity' index='idx' of={linkListEntities}>
          <SubNavigationList listEntity={listEntity} key={idx} />
        </For>
      </div>
      <If condition={image}>
        <div sx={{ flexBasis: '30%', marginTop: [0, '-xxl', '-xxl'] }}>
          <ImageModule sx={imageStyles} {...imageEntity} critical={true} />
        </div>
      </If>
    </div>
  )
}

const SubNavigationList = ({ listEntity: { links, subtitle } }) => {
  const linkEntities = useEntities(links)

  return (
    <div
      sx={{
        display: 'flex',
        flexDirection: 'column',
        'span:last-of-type': {
          marginBottom: ['md', 'lg', 'lg'],
        },
      }}
    >
      <If condition={subtitle}>
        <Type.H5 sx={{ marginBottom: ['md', 'md', 'lg'] }}>{subtitle}</Type.H5>
      </If>
      <For each='link' of={linkEntities}>
        <SubNavigationLink link={link} />
      </For>
    </div>
  )
}

const SubNavigationLink = ({ link }) => {
  const contentType = contentTypeId(link)
  const isUrl = contentType === 'url'

  return (
    <Choose>
      <When condition={isUrl}>
        <UrlLinkCard link={link} />
      </When>
      <Otherwise>
        <LinkListLinkCard link={link} />
      </Otherwise>
    </Choose>
  )
}

const UrlLinkCard = ({ link }) => {
  const { linkText, variants: pages, url } = link
  const baseUrl = useMeta(['authorSchema', 'organizationUrl']).slice(0, -1)

  const pageEntities = useEntities(pages)
  const filteredPages = pageEntities.filter(variant => {
    return contentTypeId(variant) === 'richPage' || contentTypeId(variant) === 'modularPage'
  })

  const linkHref = `${baseUrl + url}`
  const title = linkText || filteredPages[0].title

  return <LinkItem linkHref={linkHref} title={title} />
}

const LinkListLinkCard = ({ link }) => {
  const { title, url, urlRef } = link
  const baseUrl = useMeta(['authorSchema', 'organizationUrl']).slice(0, -1)

  const urlRefEntity = useEntity(urlRef)

  const linkHref = urlRef ? `${baseUrl + urlRefEntity.url}` : url
  return <LinkItem linkHref={linkHref} title={title} />
}

const LinkItem = ({ linkHref, title }) => {
  return (
    <span sx={{ marginBottom: 'md' }}>
      <Type.Body2
        as='a'
        href={linkHref}
        sx={{
          textDecoration: 'none',
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          paddingBottom: '1px',
          color: 'grey-100',
          variant: stylePath('link.text', 'collectionOfLinks'),
        }}
      >
        {title}
      </Type.Body2>
    </span>
  )
}

export default SubNavigationCollection
